/**
 * Leadler › Harita charts (v0.1.80): the saturation curve of a region (distinct companies
 * against query runs in execution order, the Chao1 estimate as the ceiling) and the source
 * contribution and overlap bars (companies only one provider found against shared ones).
 * Plain SVG and HTML; company-level data only.
 */

import { type LeadListItem, PROVIDER_LABELS, type ScanCurve, type SourceStat } from '../api/leads.js';
import { formatNumber, formatPct } from '../lib/format.js';

const W = 360;
const H = 180;
const PAD = { l: 36, r: 12, t: 14, b: 26 };
/** Below this many resolved companies Chao1 says little; the tile and the chart say so. */
export const COVERAGE_MIN_COMPANIES = 20;

export function SaturationCurve({ curve }: { curve: ScanCurve | null }) {
  if (curve === null || curve.points.length < 2) {
    return (
      <section className="sc" aria-label="Doygunluk eğrisi">
        <h3 className="sc__title">Doygunluk eğrisi</h3>
        <p className="placeholder-text">Bu bölgede henüz çözümlenmiş sorgu sonucu yok.</p>
      </section>
    );
  }
  const last = curve.points[curve.points.length - 1];
  const maxN = Math.max(1, last?.n ?? 1);
  const ceiling = Math.max(1, curve.observed, Math.ceil(curve.estimate));
  const x = (n: number) => PAD.l + ((W - PAD.l - PAD.r) * n) / maxN;
  const y = (companies: number) => H - PAD.b - ((H - PAD.t - PAD.b) * companies) / ceiling;
  const d = curve.points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.n).toFixed(1)} ${y(point.companies).toFixed(1)}`)
    .join(' ');
  const enough = curve.observed >= COVERAGE_MIN_COMPANIES;
  const summary = `${formatNumber(maxN)} sorgu koşusunda ${formatNumber(curve.observed)} firma; Chao1 tahmini ${formatNumber(Math.round(curve.estimate))}`;
  return (
    <section className="sc" aria-label="Doygunluk eğrisi">
      <h3 className="sc__title">Doygunluk eğrisi</h3>
      <p className="sc__lead ink-2">
        Sorgular koşuldukları sırayla; dikey eksen çözümlenen tekil firma. Kesikli çizgi Chao1 tahmini: eğri ona
        yaklaştıkça yeni sorgu az firma getirir.
      </p>
      <svg className="sc__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={summary}>
        <line className="sc__axis" x1={PAD.l} y1={H - PAD.b} x2={W - PAD.r} y2={H - PAD.b} />
        <line className="sc__axis" x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={H - PAD.b} />
        {enough ? (
          <>
            <line className="sc__estimate" x1={PAD.l} x2={W - PAD.r} y1={y(curve.estimate)} y2={y(curve.estimate)} />
            <text className="sc__label" x={W - PAD.r} y={y(curve.estimate) - 4} textAnchor="end">
              tahmin {formatNumber(Math.round(curve.estimate))}
            </text>
          </>
        ) : null}
        <path className="sc__line" d={d} />
        {last === undefined ? null : <circle className="sc__dot" cx={x(last.n)} cy={y(last.companies)} r={3.5} />}
        <text className="sc__tick" x={PAD.l} y={H - 8}>
          0
        </text>
        <text className="sc__tick" x={W - PAD.r} y={H - 8} textAnchor="end">
          {formatNumber(maxN)} sorgu
        </text>
        <text className="sc__tick" x={PAD.l - 4} y={PAD.t + 4} textAnchor="end">
          {formatNumber(ceiling)}
        </text>
      </svg>
      <p className="sc__note ink-2">
        {formatNumber(curve.observed)} firma görüldü; {curve.f1} tanesi tek sorguda, {curve.f2} tanesi iki sorguda
        çıktı.{' '}
        {enough
          ? `Chao1 tahmini ${formatNumber(Math.round(curve.estimate))} firma, kapsama ${formatPct(curve.coverage)}.`
          : `Tahmin için en az ${COVERAGE_MIN_COMPANIES} firma gerekir.`}
      </p>
    </section>
  );
}

export function SourceOverlap({ companies, sources }: { companies: LeadListItem[]; sources: SourceStat[] }) {
  const providers = new Set<string>(sources.map((source) => source.provider));
  for (const company of companies) for (const provider of company.providers) providers.add(provider);
  const rows = [...providers]
    .map((provider) => {
      const found = companies.filter((company) => company.providers.includes(provider));
      const only = found.filter((company) => company.providers.length === 1).length;
      const stat = sources.find((source) => source.provider === provider);
      return {
        provider,
        found: found.length,
        only,
        shared: found.length - only,
        queries: stat?.queries ?? 0,
        results: stat?.results ?? 0,
      };
    })
    .sort((a, b) => b.found - a.found || b.queries - a.queries);
  const max = Math.max(1, ...rows.map((row) => row.found));
  const traced = companies.filter((company) => company.providers.length > 0).length;
  return (
    <section className="so" aria-labelledby="so-title">
      <h3 id="so-title" className="sc__title">
        Kaynak katkısı ve çakışma
      </h3>
      <p className="sc__lead ink-2">
        Her kaynağın bulduğu firma; koyu kısım yalnız o kaynağın bulduğu, açık kısım başka bir kaynakla ortak.
        {traced === 0 ? ' Bu bölgede kaynak izi taşıyan firma yok.' : ''}
      </p>
      {rows.length === 0 ? (
        <p className="placeholder-text">Bu bölgede sorgu koşmadı.</p>
      ) : (
        <ul className="so__list" aria-label="Kaynak katkısı">
          {rows.map((row) => (
            <li key={row.provider} className="so__row">
              <span className="so__name">{PROVIDER_LABELS[row.provider] ?? row.provider}</span>
              <span className="so__track" aria-hidden="true">
                <span className="so__only" style={{ width: `${(100 * row.only) / max}%` }} />
                <span className="so__shared" style={{ width: `${(100 * row.shared) / max}%` }} />
              </span>
              <span className="so__num mono">
                {row.found} <span className="ink-2">({row.only} yalnız)</span>
              </span>
              <span className="so__meta ink-2">
                {formatNumber(row.queries)} sorgu · {formatNumber(row.results)} sonuç
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
